import { z } from "zod";
import { CHART_SECTIONS, type ChartSection } from "./charts";
import { groupDiscountTiersSchema, type GroupDiscountTier, type PrintSheet, settingsSchema } from "./settings";

/** Catalog item types. Packages and memberships are reserved for Phase 2. */
export const ITEM_TYPES = ["service", "add_on", "product", "package", "membership"] as const;
export type ItemType = (typeof ITEM_TYPES)[number];
/** Types that have management screens now. */
export const MANAGED_ITEM_TYPES = ["service", "add_on", "product"] as const;
export type ManagedItemType = (typeof MANAGED_ITEM_TYPES)[number];

export const ITEM_TYPE_LABELS: Record<ItemType, { one: string; many: string }> = {
  service: { one: "Service", many: "Services" },
  add_on: { one: "Add-on", many: "Add-ons" },
  product: { one: "Product", many: "Products" },
  package: { one: "Package", many: "Packages" },
  membership: { one: "Membership", many: "Memberships" },
};

/** Price contexts besides the base (clinic) price. */
export const PRICE_CONTEXTS = ["mobile", "event"] as const;
export type PriceContext = (typeof PRICE_CONTEXTS)[number];

const minutes = z
  .number()
  .int()
  .min(0)
  .max(720)
  .refine((m) => m % 5 === 0, "Use 5-minute steps");
const cents = z.number().int().min(0, "Prices can't be negative").max(10_000_000);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex color like #C51A6A");

// ── Locations ───────────────────────────────────────────────────────────

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time like 09:30");
export const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const fromMinutes = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export const locationHoursSchema = z
  .array(z.object({ weekday: z.number().int().min(0).max(6), opens: hhmm, closes: hhmm }))
  .max(50)
  .superRefine((rows, ctx) => {
    rows.forEach((r, i) => {
      if (toMinutes(r.closes) <= toMinutes(r.opens)) ctx.addIssue({ code: "custom", path: [i, "closes"], message: "Closing time must be after opening time" });
    });
    for (let d = 0; d < 7; d++) {
      const day = rows.filter((r) => r.weekday === d).sort((a, b) => toMinutes(a.opens) - toMinutes(b.opens));
      for (let i = 1; i < day.length; i++) {
        if (toMinutes(day[i]!.opens) < toMinutes(day[i - 1]!.closes)) ctx.addIssue({ code: "custom", message: "Open hours on the same day can't overlap" });
      }
    }
  });

/** A day (or part of one) a location is closed. */
export const closureInputSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-12-25"),
    label: z.string().trim().max(80).default(""),
    /** Part of the day only; null = all day. */
    start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().default(null),
    end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/).nullable().default(null),
  })
  .refine((c) => (c.start === null) === (c.end === null) && (c.start === null || c.start < c.end!), { message: "Enter both times, the end after the start (or neither for all day)", path: ["end"] });

export const locationInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  addressLine1: z.string().trim().min(1).max(120),
  addressLine2: z.string().trim().max(120).default(""),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(2).max(40),
  postalCode: z.string().trim().min(3).max(12),
  phone: z.string().trim().max(30).nullable().default(null),
  /** Sales tax percent, e.g. 8.625. VV (Oklahoma) is 0. */
  taxRatePercent: z.number().min(0).max(25).default(0),
  active: z.boolean().default(true),
  hours: locationHoursSchema.default([]),
  closures: z
    .array(closureInputSchema)
    .max(200)
    .refine((c) => new Set(c.map((x) => x.date)).size === c.length, "Each closure needs a different date")
    .default([]),
});
export type LocationInput = z.infer<typeof locationInputSchema>;

export interface LocationItem {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string | null;
  taxRatePercent: number;
  active: boolean;
  hours: { weekday: number; opens: string; closes: string }[];
  closures: { date: string; label: string; start: string | null; end: string | null }[];
}

// ── Categories ──────────────────────────────────────────────────────────

export const categoryInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).default(""),
  /** A person gets at most one service from this category per visit (one IV drip). */
  onePerVisit: z.boolean().default(false),
});

export interface CategoryItem {
  id: string;
  name: string;
  description: string;
  onePerVisit: boolean;
  sortOrder: number;
  itemCount: number;
}

// ── Catalog items ───────────────────────────────────────────────────────

export const groupDiscountModeSchema = z.enum(["default", "custom", "none"]);
export type GroupDiscountMode = z.infer<typeof groupDiscountModeSchema>;

const customTiersSchema = z.object({
  clinic: groupDiscountTiersSchema.default([]),
  mobile: groupDiscountTiersSchema.default([]),
  event: groupDiscountTiersSchema.default([]),
});

export const catalogItemInputSchema = z
  .object({
    type: z.enum(MANAGED_ITEM_TYPES),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(1000).default(""),
    categoryId: z.string().uuid().nullable().default(null),
    showOnPublicMenu: z.boolean().default(true),
    availableClinic: z.boolean().default(true),
    availableMobile: z.boolean().default(true),
    /** Services: can run on top of other services on the same visit (compression during a drip). */
    runsAlongside: z.boolean().default(false),
    priceCents: cents,
    /** Null means "same as the clinic price". */
    mobilePriceCents: cents.nullable().default(null),
    eventPriceCents: cents.nullable().default(null),
    taxable: z.boolean().default(false),

    // Services: start (staff and resource busy) + working (resource busy, staff free) + cleanup.
    // Add-ons: workingMinutes is the time they add to the service.
    startMinutes: minutes.default(0),
    workingMinutes: minutes.default(0),
    cleanupMinutes: minutes.default(0),
    color: hexColor.nullable().default(null),
    allowedRoleIds: z.array(z.string().uuid()).max(50).default([]),
    groupDiscountMode: groupDiscountModeSchema.default("default"),
    groupDiscountCustom: customTiersSchema.default({ clinic: [], mobile: [], event: [] }),

    // Add-ons: which services they attach to.
    attachToCategoryIds: z.array(z.string().uuid()).max(50).default([]),
    attachToServiceIds: z.array(z.string().uuid()).max(200).default([]),

    // Charts and forms (chunk C). Null chart section = automatic; null forms = every active form.
    chartSection: z.enum(CHART_SECTIONS).nullable().default(null),
    requiredFormKeys: z.array(z.string().max(40)).max(20).nullable().default(null),
    /** The client needs a current good faith exam for this service. */
    needsGfe: z.boolean().default(false),
    /** Which Qualiphy exam (null = the clinic's default exam). */
    gfeExamId: z.number().int().positive().nullable().default(null),

    // Products.
    sku: z.string().trim().max(60).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.type === "service" && v.startMinutes + v.workingMinutes <= 0) {
      ctx.addIssue({ code: "custom", path: ["startMinutes"], message: "A service needs some start or working time" });
    }
    // Neither clinic nor mobile = only offered through event menus.
  });
export type CatalogItemInput = z.infer<typeof catalogItemInputSchema>;

export interface CatalogItem {
  id: string;
  type: ItemType;
  name: string;
  description: string;
  categoryId: string | null;
  status: "active" | "archived";
  showOnPublicMenu: boolean;
  availableClinic: boolean;
  availableMobile: boolean;
  runsAlongside: boolean;
  priceCents: number;
  mobilePriceCents: number | null;
  eventPriceCents: number | null;
  taxable: boolean;
  startMinutes: number;
  workingMinutes: number;
  cleanupMinutes: number;
  color: string | null;
  allowedRoleIds: string[];
  /** Worked out from recipes: add-ons whose medications are all already in this service. */
  includedAddOnIds: string[];
  /** The medications in its recipe (not fluids or supplies). */
  medications: { id: string; name: string }[];
  /** Services: its category allows one per person per visit (the category's name), else null. */
  onePerVisitCategory: string | null;
  recipe: RecipeLine[];
  /** Equipment needed for clinic visits (see scheduling.ts). */
  requirements: { typeId: string | null; resourceId: string | null; quantity: number }[];
  groupDiscountMode: GroupDiscountMode;
  groupDiscountCustom: { clinic: GroupDiscountTier[]; mobile: GroupDiscountTier[]; event: GroupDiscountTier[] };
  attachToCategoryIds: string[];
  attachToServiceIds: string[];
  sku: string | null;
  /** A photo for the clinic's website feed. */
  photoKey: string | null;
  /** Set on the item (null = automatic). */
  chartSection: ChartSection | null;
  /** What charts actually use: the setting, or the automatic choice. */
  effectiveChartSection: ChartSection;
  /** Where it goes when chartSection is left on Automatic. */
  autoChartSection: ChartSection;
  /** Null = every active form. */
  requiredFormKeys: string[] | null;
  needsGfe: boolean;
  /** Null = the clinic's default exam. */
  gfeExamId: number | null;
  sortOrder: number;
  updatedAt: string;
}

/** Total minutes a client sees for a service. */
export const totalMinutes = (i: Pick<CatalogItem, "startMinutes" | "workingMinutes" | "cleanupMinutes">) =>
  i.startMinutes + i.workingMinutes + i.cleanupMinutes;

// ── Formulary and recipes ───────────────────────────────────────────────

export const FORMULARY_KINDS = ["medication", "fluid", "supply"] as const;
export type FormularyKind = (typeof FORMULARY_KINDS)[number];
export const DOSE_UNITS = ["mg", "mcg", "iu", "units", "ml", "each"] as const;
export type DoseUnit = (typeof DOSE_UNITS)[number];
export const DOSE_UNIT_LABELS: Record<DoseUnit, string> = { mg: "mg", mcg: "mcg", iu: "IU", units: "units", ml: "mL", each: "each" };
/** Units measured by a strength per mL (so mL can be calculated). */
export const needsStrength = (u: DoseUnit) => u !== "ml" && u !== "each";
export const ROUTES = ["iv", "iv_push", "im", "sq", "im_sq"] as const;
export type Route = (typeof ROUTES)[number];
export const ROUTE_LABELS: Record<Route, string> = { iv: "IV", iv_push: "IV push", im: "IM", sq: "SQ", im_sq: "IM/SQ" };

/** Units an ingredient of a compounded product can be listed in (per mL). */
export const COMPONENT_UNITS = ["mg", "mcg", "iu", "units"] as const;
export type ComponentUnit = (typeof COMPONENT_UNITS)[number];

export const formularyComponentSchema = z.object({
  name: z.string().trim().min(1).max(80),
  amountPerMl: z.number().positive().max(1_000_000),
  unit: z.enum(COMPONENT_UNITS),
});
export type FormularyComponent = z.infer<typeof formularyComponentSchema>;

export const formularyInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    drugName: z.string().trim().max(160).default(""),
    kind: z.enum(FORMULARY_KINDS),
    doseUnit: z.enum(DOSE_UNITS),
    /** Dose units per mL (e.g. 300 for magnesium chloride 300 mg/mL). Null for mL and each. */
    strengthPerMl: z.number().positive().max(1_000_000).nullable().default(null),
    /** Usual vial (or bag) size in mL; the default when receiving. */
    vialSizeMl: z.number().positive().max(100_000).nullable().default(null),
    /** Vial colour on the inventory shelf; null picks one from the name. */
    color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .default(null),
    /** Low stock at or below this many vials (bags, or items for "each"); null = no alert. */
    reorderAt: z.number().min(0).max(100_000).nullable().default(null),
    notes: z.string().trim().max(500).default(""),
    active: z.boolean().default(true),
    /** Used in care, never sold (lidocaine): left out of the printed menu's stock check. */
    clinicalOnly: z.boolean().default(false),
    /** For compounded products measured in mL: what each mL contains. */
    components: z.array(formularyComponentSchema).max(30).default([]),
  })
  .superRefine((v, ctx) => {
    if (needsStrength(v.doseUnit) && v.strengthPerMl === null) ctx.addIssue({ code: "custom", path: ["strengthPerMl"], message: "Enter the strength per mL" });
    if (v.components.length && v.doseUnit !== "ml") ctx.addIssue({ code: "custom", path: ["components"], message: "Only products measured in mL can list contents" });
  })
  .transform((v) => ({ ...v, strengthPerMl: needsStrength(v.doseUnit) ? v.strengthPerMl : null }));
export type FormularyInput = z.infer<typeof formularyInputSchema>;

export interface FormularyItem {
  id: string;
  name: string;
  drugName: string;
  kind: FormularyKind;
  doseUnit: DoseUnit;
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  color: string | null;
  reorderAt: number | null;
  notes: string;
  active: boolean;
  clinicalOnly: boolean;
  sortOrder: number;
  components: FormularyComponent[];
}

export const recipeInputSchema = z.object({
  lines: z
    .array(
      z.object({
        formularyItemId: z.string().uuid(),
        dose: z.number().positive("Dose must be more than 0").max(1_000_000),
        route: z.enum(ROUTES).nullable().default(null),
        /** How often it can be given, as the menu shows it ("1/wk", "3/mo"). */
        frequency: z
          .string()
          .trim()
          .max(20)
          .nullable()
          .default(null)
          .transform((v) => v || null),
      }),
    )
    .max(60),
});
export type RecipeInput = z.infer<typeof recipeInputSchema>;

export interface RecipeLine {
  formularyItemId: string;
  dose: number;
  route: Route | null;
  frequency: string | null;
}

/** mL to draw for a dose, to a tenth; null when it can't be measured in mL (each). */
export function doseToMl(f: Pick<FormularyItem, "doseUnit" | "strengthPerMl">, dose: number): number | null {
  if (f.doseUnit === "each") return null;
  if (f.doseUnit === "ml") return Math.round(dose * 10) / 10;
  if (!f.strengthPerMl) return null;
  return Math.round((dose / f.strengthPerMl) * 10) / 10;
}

/** On a U-100 insulin syringe, 1 unit = 0.01 mL. Shown for small draws so syringe units aren't mistaken for a drug amount. */
export const syringeUnits = (ml: number) => Math.round(ml * 100);

/** What a dose of a compounded product delivers: each ingredient scaled to the mL drawn. */
export function componentDoses(f: Pick<FormularyItem, "components">, ml: number): { name: string; amount: number; unit: ComponentUnit }[] {
  return f.components.map((c) => ({ name: c.name, amount: Math.round(c.amountPerMl * ml * 1000) / 1000, unit: c.unit }));
}

/** Display a dose like "1,200 mg" or "1 each". */
export function formatDose(dose: number, unit: DoseUnit): string {
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(dose)} ${DOSE_UNIT_LABELS[unit]}`;
}

/**
 * Add-ons already in a service: every medication in the add-on's recipe is in the service's recipe.
 * Fluids and supplies don't count (an extra bag is never "included").
 */
export function includedAddOns(
  service: { recipe: RecipeLine[] },
  addOns: { id: string; recipe: RecipeLine[] }[],
  formulary: Pick<FormularyItem, "id" | "kind">[],
): string[] {
  const isMed = (id: string) => formulary.find((f) => f.id === id)?.kind === "medication";
  const inService = new Set(service.recipe.filter((l) => isMed(l.formularyItemId)).map((l) => l.formularyItemId));
  return addOns
    .filter((a) => {
      const meds = a.recipe.filter((l) => isMed(l.formularyItemId));
      return meds.length > 0 && meds.every((l) => inService.has(l.formularyItemId));
    })
    .map((a) => a.id);
}

/**
 * What breaks the visit rules for one person: two services from a "one per person" category (two
 * drips), or the same medication twice (in two services, or a service and an add-on that isn't
 * already included in it). Online booking refuses these; staff can go ahead with a reason.
 */
export function visitConflicts(lines: { service: Pick<CatalogItem, "id" | "name" | "includedAddOnIds" | "medications" | "onePerVisitCategory">; addOns: Pick<CatalogItem, "id" | "name" | "medications">[] }[]): string[] {
  const out: string[] = [];
  const byCategory = new Map<string, string[]>();
  for (const l of lines) if (l.service.onePerVisitCategory) byCategory.set(l.service.onePerVisitCategory, [...(byCategory.get(l.service.onePerVisitCategory) ?? []), l.service.name]);
  for (const [category, names] of byCategory) if (names.length > 1) out.push(`Only one from ${category} per person: ${names.join(" and ")}.`);
  // Everything that brings its own medications: each service, and its add-ons that aren't already in it.
  const parts = lines.flatMap((l) => [l.service, ...l.addOns.filter((a) => !l.service.includedAddOnIds.includes(a.id))]);
  const seen = new Map<string, { med: string; item: string }>();
  const twice = new Set<string>();
  for (const p of parts) {
    for (const m of p.medications) {
      const first = seen.get(m.id);
      if (first && first.item !== p.name && !twice.has(m.id)) {
        twice.add(m.id);
        out.push(`${m.name} is in both ${first.item} and ${p.name}.`);
      } else if (!first) seen.set(m.id, { med: m.name, item: p.name });
    }
  }
  return out;
}

// ── Public menu ─────────────────────────────────────────────────────────

export interface PublicPlan {
  id: string;
  name: string;
  description: string;
  /** Per month for memberships; once for packages. */
  priceCents: number;
  benefits: string[];
  minimumMonths: number | null;
  /** The online sign-up page. */
  signUpUrl: string;
}

// ── Money ───────────────────────────────────────────────────────────────

export function formatCents(c: number | null | undefined): string {
  if (c === null || c === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(c / 100);
}

/** Parse "$1,234.50" or "99" into cents; null if not a valid amount. */
export function parseDollars(s: string): number | null {
  const clean = s.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{0,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
}

/**
 * Automatic chart section: services given by IV are the cocktail, other services
 * are injections (vitamins); add-ons are add-ons. Medications are set on the item.
 * Anything with nothing in its recipe in a category named for recovery (boots, chairs, wraps) is recovery.
 */
export function autoChartSection(item: Pick<CatalogItem, "type" | "recipe">, fluidIds: ReadonlySet<string>, categoryName = ""): ChartSection {
  if (item.type !== "product" && !item.recipe.length && /recover/i.test(categoryName)) return "recovery";
  // A bag of fluid makes it a drip, even with nothing else in it (The Base).
  if (item.type === "service") return item.recipe.some((l) => l.route === "iv" || l.route === "iv_push" || fluidIds.has(l.formularyItemId)) || item.recipe.length === 0 ? "cocktail" : "vitamins";
  if (item.type === "add_on") return "addons";
  return "hidden";
}

// ── Printed menu ─────────────────────────────────────────────────────────

/** Catalog → Print menu: the sheets, and which formulary items have stock on hand. */
export interface PrintMenuView {
  sheets: PrintSheet[];
  /** Formulary items with stock on the shelf. */
  inStock: string[];
}

export const printMenuInputSchema = z.object({ sheets: settingsSchema.shape["menu.printSheets"] });
